import { onScopeDispose, ref, shallowRef, type Ref, type ShallowRef } from 'vue';

export interface AsyncData<T> {
  data: ShallowRef<T | null>;
  error: ShallowRef<unknown>;
  loading: Ref<boolean>;
  reload: () => Promise<void>;
}

/**
 * Load data with loading/error state and cancellation. A newer load() aborts
 * the previous one, so a stale response never overwrites a fresh one.
 */
export function useAsyncData<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  { immediate = true }: { immediate?: boolean } = {},
): AsyncData<T> {
  const data = shallowRef<T | null>(null);
  const error = shallowRef<unknown>(null);
  const loading = ref(false);
  let controller: AbortController | null = null;

  async function reload(): Promise<void> {
    controller?.abort();
    const current = new AbortController();
    controller = current;
    loading.value = true;
    error.value = null;
    try {
      const result = await loader(current.signal);
      if (controller === current) data.value = result;
    } catch (err) {
      if ((err as { name?: string } | null)?.name !== 'AbortError' && controller === current)
        error.value = err;
    } finally {
      if (controller === current) loading.value = false;
    }
  }

  onScopeDispose(() => controller?.abort());
  if (immediate) void reload();

  return { data, error, loading, reload };
}
